import {Injectable} from '@angular/core';
import {Platform} from 'ionic-angular';
import {SpeechRecognition} from '@ionic-native/speech-recognition';
import {TextToSpeech} from '@ionic-native/text-to-speech';
import {AndroidPermissions} from '@ionic-native/android-permissions';
import * as _ from 'lodash';

import {ThinningMeasurement} from "../models/thinning-measurement";
import {Thinning} from "../providers/thinning";
import {ThinningMeasurementDetailPage} from "../pages/thinning-measurement-detail/thinning-measurement-detail";

/**
 * Service for speech recognition and TTS functions, used by ThinningMeasurementDetailPage component
 */

const listeningForValue = 'VALUE';
const listeningForCluster = 'CLUSTER';

@Injectable()
export class Speech {

    measurement: ThinningMeasurement;
    previousMeasurement: ThinningMeasurement;
    cluster_count: number;
    flower_count: number;
    detailPage: ThinningMeasurementDetailPage;
    matches: string;
    currentSpeechState: SpeechState;

    constructor(public thinning: Thinning,
                private speechRecognition: SpeechRecognition,
                private tts: TextToSpeech,
                private platform: Platform,
                private permissions: AndroidPermissions) {
        // debounce this function to enable handling of multiple matches on iOS
        this.invokeHandler = _.debounce(this.invokeHandler, 500);
    }

    setMeasurements(measurement: ThinningMeasurement,
                    previousMeasurement: ThinningMeasurement,
                    cluster_count: number,
                    flower_count: number,
                    detailPage: ThinningMeasurementDetailPage) {
        this.measurement = measurement;
        this.previousMeasurement = previousMeasurement;
        this.cluster_count = cluster_count;
        this.flower_count = flower_count;
        this.detailPage = detailPage;
    }

    canDoSpeech() {
        return this.platform.is('cordova');
    }

    say(text) {
        console.log('say: ' + text);
        const rate = (this.platform.is('ios') ? 1.6 : 1);
        return this.tts.speak({text: text, rate: rate}); // return promise
    }

    isListening() {
        return this.currentSpeechState != null;
    }

    // NB: we don't disable the microphone button, as that causes the focus to go to the neighboring
    // input, which brings up the keyboard. Instead we just ignore repeat clicks if we're already listening.
    beginListening(t, c, f) {
        if (this.isListening()) {
            return;
        }
        console.log('beginListening');

        this.currentSpeechState = new SpeechState(t, c, f,
            this.measurement,
            _.toNumber(this.cluster_count),
            _.toNumber(this.flower_count)
        );

        if (this.platform.is('ios')) {
            this.speechRecognition.hasPermission()
                .then((hasPermission: boolean) => {
                    if (!hasPermission) {
                        this.speechRecognition.requestPermission();
                    }
                    this.listen();
                });
        } else if (this.platform.is('android')) {
            this.permissions.requestPermission(this.permissions.PERMISSION.RECORD_AUDIO)
                .then(
                    () => {
                        this.listen();
                    },
                    () => {
                    }
                );
        }
    }

    ceaseListening() {
        console.log('----------- ceaseListening');
        this.speechRecognition.stopListening();
        this.currentSpeechState = null;
        // TODO: update UI
        this.say('Recognition stopped');
    }

    // main speech loop - speak feedback, speak prompt, listen for input, handle input, rinse and repeat
    listen() {
        this.say(this.currentSpeechState.feedback).then(() => {
            this.say(this.currentSpeechState.prompt).then(() => {
                const listenSubscription = this.speechRecognition.startListening({showPartial: true, showPopup: false})
                    .subscribe(
                        matches => {
                            this.invokeHandler(listenSubscription, matches);
                        },
                        (error) => { // recognition timed out - restart loop
                            this.speechRecognition.stopListening();
                            this.currentSpeechState.feedback = 'Listening.';
                            this.listen();
                        }
                    );
            })
        });
    }

    // NB: this function is debounced in the constructor, so that it is only invoked after a designated interval -
    // on iOS the listenSubscription will return multiple partial matches (e.g. "20" then "23" when we say "23"),
    // so we need to wait for the last (hopefully best) match and then stop recognition manually.
    invokeHandler(listenSubscription, matches) {
        console.log('----------- invokeHandler');
        listenSubscription.unsubscribe(); // don't accept further results, we just want the first one

        this.speechRecognition.stopListening().then(
            success => {
                this.matches = matches.join(', '); // to show on debug tab
                const input = (matches.length > 0 ? matches[0] : null);
                console.log('----------- matches', matches);

                const keepGoing = this.handleInput(input); // false if stop command handled
                if (keepGoing) {
                    this.listen();
                }
            },
            error => {
                console.log('----------- stop error');
            },
        );
    }

    // handle a single input recognized from the user's speech
    // return false if it's a stop request; true otherwise
    handleInput(input: string) {
        const ss = this.currentSpeechState;
        let doneMeasuring = false;
        console.log('----------- handleInput: ' + input);

        if (input.toLowerCase() == 'stop') { // break main loop
            this.ceaseListening();
            return false;
        }

        if (input === null) {
            ss.feedback = 'Not recognized.';
        } else if (input.toLowerCase() == 'help') {
            ss.feedback = ss.getHelpSummary();
        } else if (input.toLowerCase() == 'skip') {
            ss.feedback = 'Skip.';
            ss.prompt = 'Say next cluster number.';
            ss.listeningFor = listeningForCluster;
        } else if (input.toLowerCase() == 'back') {
            ss.listeningFor = listeningForValue; // may have been listening for next cluster
            ss.f--;
            ss.feedback = 'Back.';
            ss.prompt = `Fruitlet ${ss.f}.`;
        } else {
            // get cluster number or measurement value
            const num = this.getNumber(input);

            if (!num) {
                ss.feedback = 'Not Recognized.';
            } else if (ss.listeningFor == listeningForValue) {
                doneMeasuring = this.handleMeasurementValue(num, ss);
            } else if (ss.listeningFor == listeningForCluster) {
                this.handleClusterJump(num, ss);
            }
        }

        if (doneMeasuring) {
            this.say(ss.feedback).then(() => {
                this.say('All clusters have been measured.').then(() => {
                    this.ceaseListening();
                });
            });
            return false; // break main loop
        } else {
            return true;
        }
    }

    // returns true if all clusters for the current tree have been measured
    handleMeasurementValue(num: number, ss: SpeechState) {
        ss.feedback = `${num}. `;
        if (num > 50 || num < 0) {
            ss.feedback += 'This value is outside the normal range.';
        }
        ss.measurement.data[ss.t + '_' + ss.c + '_' + ss.f] = num;
        this.detailPage.autosave();

        // move to next cluster or next fruitlet
        // (if we've done the last flower for the cluster OR
        // there was no data for the next flower in the previous measurement, indicating that it's gone)
        ss.f++;
        if (ss.f > ss.flowerCount ||
            !this.thinning.measurementHasDataForFlower(this.previousMeasurement, ss.t, ss.c, ss.f)) {
            // check if done measuring (NB: we only do this at the end of a cluster, not after every flower)
            if (this.thinning.measurementHasDataForAllClusters(this.measurement, ss.t, this.cluster_count)) {
                return true; // done measuring
            } else {
                ss.prompt = 'Say next cluster number.';
                ss.listeningFor = listeningForCluster;
            }
        } else {
            ss.prompt = `Fruitlet ${ss.f}.`;
        }
        return false; // not done measuring
    }

    handleClusterJump(num: number, ss: SpeechState) {
        if (num > ss.clusterCount) {
            ss.feedback = num + ': no such cluster.';
            ss.prompt = 'Say next cluster number.';
        } else if (this.thinning.measurementHasDataForCluster(this.measurement, ss.t, num)) {
            ss.feedback = `Cluster ${num} has already been measured.`;
            ss.prompt = 'Say next cluster number.';
        } else {
            this.detailPage.currentCluster = num - 1; // tab index is 0-based (see thinning-measurement-detail.html)
            this.detailPage.cd.detectChanges(); // refresh current cluster tab
            console.log('currentCluster', this.detailPage.currentCluster);
            ss.c = num;
            ss.f = 1;
            ss.feedback = ss.getClusterSummary(true);
            ss.prompt = `Fruitlet ${ss.f}.`;
            ss.listeningFor = listeningForValue;
        }
    }

    // recognize a number from common matches
    getNumber(input: string) {
        console.log('getNum: input: ', input);
        const num = _.toNumber(input);
        if (!_.isNaN(num)) {
            console.log('getNum: !isNaN case: ', num);
            return num;
        } else {
            console.log('getNum: other case: ', input);
            const options = {
                1: ['one', 'won'],
                2: ['two', 'to', 'too'],
                3: ['three', 'tree', 'free'],
                4: ['four', 'for', 'fore'],
                5: ['five'],
                6: ['six', 'sex'],
                7: ['seven', 'seventh'],
                8: ['eight', 'ate'],
                9: ['nine'],
            };
            let val = null;
            _.each(_.keys(options), (n) => {
                const i = options[n].indexOf(input.toLowerCase());
                if (i != -1) {
                    console.log('getNum: matched: ', n);
                    val = n;
                }
            });
            return val;
        }
    }
}

// -------------------------------------------

class SpeechState {

    feedback: string = '';
    prompt: string = '';
    listeningFor: string = listeningForValue;

    constructor(public t: number,
                public c: number,
                public f: number,
                public measurement: ThinningMeasurement,
                public clusterCount: number,
                public flowerCount: number) {
        // initial prompt
        // NB: not warning here if cluster already has measurements
        this.prompt = `Cluster ${this.c}, Fruitlet ${this.f}. `;
    }

    getHelpSummary() {
        if (this.listeningFor == listeningForCluster) {
            return 'Say next cluster number.';
        } else {
            // TODO: describe how many clusters/fruitlets have been measured
            let summary = `Tree ${this.t}, Cluster ${this.c}`;
            for (let i = 1; i <= this.flowerCount; i++) {
                const val = this.measurement.data[`${this.t}_${this.c}_${i}`];
                if (val) {
                    summary += `Fruitlet ${i}, ${val}. `;
                }
            }
            return summary;
        }
    }

    // shorter summary for starting a cluster, with prompt for next (first) fruitlet
    getClusterSummary(withCurrentValues) {
        let summary = `Cluster ${this.c}. `;

        if (withCurrentValues) {
            let vals = [];
            for (let i = 1; i <= this.flowerCount; i++) {
                const val = this.measurement.data[`${this.t}_${this.c}_${i}`];
                if (val) {
                    vals.push(val);
                }
            }
            if (vals.length) {
                summary += `${vals.length} measurements entered.`;
            }
        }
        return summary;
    }
}



// WEBPACK FOOTER //
// ./src/providers/speech.ts